import React, { useMemo } from 'react';

import { Role, Text, View, ViewStyle } from 'react-native';

import { useTranslation } from 'react-i18next';
import { ImageStyle } from 'react-native-fast-image';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { RBChannel } from 'store/railsBuilder/reducer.types';

import getOrdinal from 'utils/getOrdinary';
import useShowImage from 'utils/hooks/useShowImage';
import { isWeb } from 'utils/responsiveness';

import NagraImage from 'components/NagraImage';
import {
    AspectRatio,
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';

import styles from './style';

type Props = {
    item: Partial<RBChannel>;
    width?: number;
    height?: number;
    style?: ViewStyle;
    logoStyle?: ImageStyle;
    isTitleVisible?: boolean;
    accessibilityRole?: string;
    accessible?: boolean;
    index?: number;
    totalNumberOfItems?: number;
    isCollection?: boolean;
    isOnSettingsPage?: boolean;
};

const {
    ACCESSIBILITY: {
        ACCESSIBILITY_COLLECTION_FIRST_ASSET,
        STRIPE: { ACCESSIBILITY_STRIPE_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;
const ChannelCard = ({
    item: channel,
    width = ImageSize.EXTRA_LARGE_CHANNEL_LOGO.width,
    height = ImageSize.EXTRA_LARGE_CHANNEL_LOGO.height,
    style,
    logoStyle,
    isTitleVisible = false,
    accessibilityRole,
    accessible = true,
    index = 0,
    totalNumberOfItems,
    isCollection = false,
    isOnSettingsPage = false,
}: Props) => {
    const {
        showImage: showChannelLogo,
        onImageLoad,
        onImageLoadError,
    } = useShowImage();

    const { t } = useTranslation();
    const title = channel?.editorial?.longName;

    const accessibilityLabel = useMemo(() => {
        if (isOnSettingsPage) {
            return (
                channel.editorial?.longName ||
                channel.editorial?.ShortName ||
                ''
            );
        }

        const firstAssetKey = isCollection
            ? ACCESSIBILITY_COLLECTION_FIRST_ASSET
            : ACCESSIBILITY_STRIPE_FIRST_ASSET;
        const orderAssetKey = 'ACCESSIBILITY_STRIPE_%NUMBER%_ASSET';

        if (index === 0) {
            return `${title} ${' '} ${t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        }

        return `${title} ${' '} ${t(orderAssetKey, {
            number: getOrdinal(index + 1),
            interpolation: {
                prefix: '%',
                suffix: '%',
                escapeValue: false,
            },
        })}`;
    }, [title, totalNumberOfItems, index, isCollection]);

    const accessibleProps = useMemo(
        () =>
            accessible && {
                accessibilityRole,
                accessibilityLabel,
                accessible,
                role: isOnSettingsPage ? ('text' as Role) : ('button' as Role),
            },
        [accessible],
    );

    return (
        <View
            style={[styles.container, style]}
            {...accessibleProps}
            {...(isWeb && { tabIndex: 0 })}
        >
            {channel && showChannelLogo && (
                <NagraImage
                    style={[styles.logo, logoStyle]}
                    contentId={channel.editorial!.id}
                    contentType={ImageContentType.CHANNEL}
                    imageType={ImageType.LOGO}
                    aspect={AspectRatio.LANDSCAPE}
                    width={width}
                    height={height}
                    resizeMode="contain"
                    onLoad={onImageLoad}
                    onError={onImageLoadError}
                />
            )}
            {title && (isTitleVisible || !showChannelLogo) && (
                <View
                    style={[
                        styles.landScapeTitlePlaceholder,
                        isTitleVisible && styles.channelLineupTitlePlaceholder,
                    ]}
                >
                    <Text
                        style={[
                            styles.landScapeTitle,
                            isTitleVisible && styles.channelLineupTitle,
                        ]}
                        numberOfLines={2}
                    >
                        {title}
                    </Text>
                </View>
            )}
        </View>
    );
};

export default ChannelCard;
